import ContextReminderContainer from '@app/components/ContextReminder'
import DeleteConfirmationModal from '@app/components/DeleteConfirmationModal'
import { NavTabItem, NavTabs } from '@app/components/NavTabs'
import MaintenancePage from '@app/routes/Maintenance/MaintenancePage'
import { ICurrentUserState } from '@app/store/modules/account'
import { bindFunctions } from '@app/utils/class'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { Link, Route, RouteComponentProps, Switch } from 'react-router-dom'
import {
  Card,
  CardBody,
  CardHeader,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from 'reactstrap'
import {
  actionCreators,
  IDevelopmentClientViewState,
} from '../modules/developmentClientDetail'

import FormGroupRenderer, {
  ILabelColumnSize,
} from '@app/components/FormGroupRenderer'
import PillListRenderer from '@app/components/PillListRenderer'
import DevelopmentClientAttachments from './DevelopmentClientAttachments'
import DevelopmentClientPhotos from './DevelopmentClientPhotos'

interface IConnectionViewRouterProps {
  clientSlug: string
}

type IComponentProps = IDevelopmentClientViewState &
  ICurrentUserState &
  RouteComponentProps<IConnectionViewRouterProps> &
  typeof actionCreators

export default class ConnectionViewPage extends React.PureComponent<
  IComponentProps
> {
  public labelColSizes: ILabelColumnSize

  constructor(props: IComponentProps) {
    super(props)

    bindFunctions(this)

    this.labelColSizes = {
      xl: 4,
    }
  }

  public componentDidMount() {
    const { clientSlug } = this.props.match.params
    this.props.loadDevelopmentClient(clientSlug)
  }

  public componentDidUpdate(
    prevProps: IComponentProps,
    prevState: any,
    snapshot: any,
  ) {
    const { clientSlug } = this.props.match.params
    if (prevProps.item && prevProps.item.slug !== clientSlug) {
      this.props.loadDevelopmentClient(clientSlug)
    }
  }

  public render() {
    if (!this.props.item || !this.props.item.id || !this.props.user) {
      return <div />
    }

    return (
      <DocumentTitle title='Development Clients'>
        <Container className='mt-3'>
          <Card className='d-flex flex-column mt-2'>
            <CardHeader className='bg-secondary text-white'>
              {this._renderCardHeader()}
            </CardHeader>
            <CardBody>{this._renderCardBody()}</CardBody>
          </Card>
          {this._renderTabs()}
          <Switch>
            <Route
              exact
              path={this.props.match.path}
              render={() => <DevelopmentClientPhotos {...this.props} />}
            />
            <Route
              exact
              path={`${this.props.match.path}/attachments`}
              render={() => <DevelopmentClientAttachments {...this.props} />}
            />
            <Route
              path={`${this.props.match.path}/reminders`}
              render={() => (
                <ContextReminderContainer
                  contextId={this.props.item.id}
                  {...this.props}
                />
              )}
            />
            <Route component={MaintenancePage} />
          </Switch>
          <DeleteConfirmationModal
            isOpen={this.props.showConfirmDelete}
            itemName={this.props.item.name}
            onConfirm={() =>
              this.props.deleteDevelopmentClient(this.props.item.id)
            }
            onToggleModal={() => this.props.toggleConfirmDelete(false)}
          />
        </Container>
      </DocumentTitle>
    )
  }

  private _renderTabs() {
    return (
      <div className='mb-3 mt-3'>
        <NavTabs>
          <NavTabItem
            badgeCount={this.props.item.photos.size}
            exact
            iconName='images'
            label='Photos'
            to={this.props.match.url}
          />
          <NavTabItem
            badgeCount={this.props.item.attachments.size}
            iconName='file'
            label='Attachments'
            to={`${this.props.match.url}/attachments`}
          />
          <NavTabItem
            iconName='bell'
            label='Reminders'
            to={`${this.props.match.url}/reminders`}
          />
        </NavTabs>
      </div>
    )
  }

  private _renderCardBody() {
    return (
      <div className='d-flex flex-column flex-wrap'>
        {this._renderLogo()}
        <FormGroupRenderer className='mt-2' labelText='Contacts'>
          <div>
            <PillListRenderer list={this.props.item.contacts} />
          </div>
        </FormGroupRenderer>
      </div>
    )
  }

  private _renderCardHeader() {
    return (
      <div className='d-flex flex-row'>
        <span>{this.props.item.name}</span>
        <div className='ml-auto'>
          <UncontrolledDropdown style={{ display: 'inline' }}>
            <DropdownToggle tag='a' className='px-1 text-center'>
              <FontAwesomeIcon icon={['fal', 'chevron-down']} fixedWidth />
            </DropdownToggle>
            {this._renderDropdown()}
          </UncontrolledDropdown>
        </div>
      </div>
    )
  }

  private _renderLogo() {
    if (!this.props.item || !this.props.item.logo) {
      return null
    }

    return this.props.item.logo.availableSizes ? (
      <img
        className='logo-image'
        src={`${this.props.item.logo.baseStorageUrl}/${
          this.props.item.logo.availableSizes.h100w100
        }`}
        width={100}
        height={100}
      />
    ) : (
      <img
        src={`${this.props.item.logo.baseStorageUrl}/${
          this.props.item.logo.id
        }${this.props.item.logo.extension}`}
        width={100}
        height={100}
      />
    )
  }

  private _renderDropdown() {
    const menuItems = []
    const item = this.props.item

    if (this._canManage()) {
      menuItems.push(
        <DropdownItem
          key='manage'
          onClick={() =>
            window.alert(
              'Manage connection access is not ready yet. Check back soon!',
            )
          }
        >
          Manage
        </DropdownItem>,
      )
    }

    if (this._canEdit()) {
      menuItems.push(
        <Link to={`/development/client/${this.props.item.slug}/edit`}>
          <DropdownItem key='edit'>Edit</DropdownItem>
        </Link>,
      )
    }

    if (this._canDelete()) {
      menuItems.push(
        <DropdownItem
          disabled={this.props.isDeleting}
          key='delete'
          onClick={() => this.props.toggleConfirmDelete(true)}
        >
          Delete
        </DropdownItem>,
      )
    }

    return <DropdownMenu>{menuItems}</DropdownMenu>
  }

  private _canDelete() {
    return this.props.permissions.canDelete === true
  }

  private _canEdit() {
    return this.props.permissions.canEdit === true
  }

  private _canManage() {
    if (!this.props.user) {
      return false
    }

    return (
      this.props.user.permissions.isAdmin ||
      this.props.user.id === this.props.item.creator
    )
  }
}
